import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import type { Deal } from '@/types'
import { toast } from 'sonner'

interface HandoffReviewModalProps {
  open: boolean
  onClose: () => void
  deal: Deal
  onAccept: () => void
  onReject: (reason: string) => void
}

export function HandoffReviewModal({
  open,
  onClose,
  deal,
  onAccept,
  onReject,
}: HandoffReviewModalProps) {
  const [rejectReason, setRejectReason] = useState('')
  const [showReject, setShowReject] = useState(false)

  const handleAccept = () => {
    onAccept()
    onClose()
  }

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Informe o motivo da rejeição')
      return
    }
    onReject(rejectReason)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Revisão de Handoff'
      description='Revise as informações passadas pelo BDR antes de aceitar o deal.'
      size='lg'
      footer={
        !showReject ? (
          <>
            <Button
              variant='destructive'
              size='sm'
              icon={<XCircle size={13} />}
              onClick={() => setShowReject(true)}
            >
              Rejeitar
            </Button>
            <Button
              variant='primary'
              size='sm'
              icon={<CheckCircle size={13} />}
              onClick={handleAccept}
            >
              Aceitar Deal
            </Button>
          </>
        ) : (
          <>
            <Button variant='ghost' size='sm' onClick={() => setShowReject(false)}>
              Voltar
            </Button>
            <Button
              variant='destructive'
              size='sm'
              onClick={handleReject}
            >
              Confirmar Rejeição
            </Button>
          </>
        )
      }
    >
      <div className='space-y-4'>
        {/* Deal summary */}
        <div className='glass-card p-4 space-y-2'>
          <div className='flex items-center justify-between'>
            <h3 className='text-sm font-bold text-text-primary'>{deal.empresaNome}</h3>
            <Badge variant='default'>{deal.nome}</Badge>
          </div>
          <p className='text-lg font-bold text-accent'>
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(deal.valor)}
          </p>
        </div>

        {/* Handoff info */}
        <div className='space-y-3'>
          <HandoffField
            label='Dores Identificadas'
            value={deal.doresIdentificadas}
            required
          />
          <HandoffField
            label='Budget Confirmado'
            value={deal.budgetConfirmado}
            required
          />
          <HandoffField
            label='Decisor Presente'
            value={deal.decisorPresente}
            required
          />
          {deal.observacoes && (
            <HandoffField label='Observações do BDR' value={deal.observacoes} />
          )}
        </div>

        {/* Reject reason */}
        {showReject && (
          <div className='space-y-2 pt-2 border-t border-white/[0.06]'>
            <div className='flex items-center gap-2 text-red-400'>
              <AlertTriangle size={14} />
              <span className='text-xs font-medium uppercase tracking-wider'>
                Motivo da Rejeição
              </span>
            </div>
            <Textarea
              placeholder='Descreva por que o handoff está sendo rejeitado...'
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

function HandoffField({
  label,
  value,
  required = false,
}: {
  label: string
  value?: string
  required?: boolean
}) {
  const isEmpty = !value || value.trim() === ''

  return (
    <div className='flex items-start justify-between gap-3'>
      <span className='text-xs text-text-label font-medium shrink-0'>{label}</span>
      {isEmpty ? (
        <div className='flex items-center gap-1.5'>
          {required && (
            <AlertTriangle size={12} className='text-amber-400' />
          )}
          <span className='text-xs text-text-label italic'>
            {required ? 'Não informado' : '—'}
          </span>
        </div>
      ) : (
        <span className='text-sm text-text-primary text-right'>{value}</span>
      )}
    </div>
  )
}
